"use client";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { endOfMonth, endOfYear, startOfMonth, startOfYear, subDays, subMonths, subYears } from "date-fns";
import { useState } from "react";

type DateRange = {
  from?: Date;
  to?: Date;
};

interface DatePickerAdvanceProps {
  date?: DateRange | Date | undefined;
  setDate: (date: DateRange | Date | undefined) => void;
}

export default function DatePickerAdvance({ date, setDate }: DatePickerAdvanceProps) {
  // Convert Date to DateRange format for Calendar component
  const dateRange: DateRange | undefined = date instanceof Date 
    ? { from: date, to: date }
    : date;
  const today = new Date();
  const yesterday = {
    from: subDays(today, 1),
    to: subDays(today, 1),
  };
  const last7Days = {
    from: subDays(today, 6),
    to: today,
  };
  const last30Days = {
    from: subDays(today, 29),
    to: today,
  };
  const monthToDate = {
    from: startOfMonth(today),
    to: today,
  };
  const lastMonth = {
    from: startOfMonth(subMonths(today, 1)),
    to: endOfMonth(subMonths(today, 1)),
  };
  const yearToDate = {
    from: startOfYear(today),
    to: today,
  };
  const lastYear = {
    from: startOfYear(subYears(today, 1)),
    to: endOfYear(subYears(today, 1)),
  };
  const [month, setMonth] = useState(today);

  return (
    <div className="rounded-md bg-white w-fit">
      <div className="flex max-sm:flex-col">
        <div className="relative max-sm:order-1 max-sm:border-t sm:w-32">
          <div className="h-full py-3 sm:border-e">
            <div className="flex flex-col px-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate({
                    from: today,
                    to: today,
                  });
                  setMonth(today);
                }}
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(yesterday);
                  setMonth(yesterday.to);
                }}
              >
                Yesterday
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(last7Days);
                  setMonth(last7Days.to);
                }}
              >
                Last 7 days
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(last30Days);
                  setMonth(last30Days.to);
                }}
              >
                Last 30 days
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(monthToDate);
                  setMonth(monthToDate.to);
                }}
              >
                Month to date
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(lastMonth);
                  setMonth(lastMonth.to);
                }}
              >
                Last month
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(yearToDate);
                  setMonth(yearToDate.to);
                }}
              >
                Year to date
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setDate(lastYear);
                  setMonth(lastYear.to);
                }}
              >
                Last year
              </Button>
            </div>
          </div>
        </div>
        <Calendar
          mode="range"
          // @ts-expect-error - react-day-picker DateRange expects required 'from', but we allow optional for better UX
          selected={dateRange}
          onSelect={(newDate) => {
            if (newDate) {
              setDate(newDate);
            }
          }}
          month={month}
          showOutsideDays={false}
          onMonthChange={setMonth}
          pagedNavigation
          className="p-2"
          disabled={[
            { after: today }, // Dates before today
          ]}
        />
      </div>
    </div>
  );
}
